<template>
  <div class="your-spouse">
    <h2 class="mb-4">Tell us about your spouse or partner</h2>

    <ValidationObserver ref="observer">
      <v-form @submit.prevent="submit">
        <v-row>
          <v-col cols="12" sm="6" md="4" class="pb-0">
            <ValidationProvider
              id="first_names"
              v-slot="{ errors }"
              name="first_names"
              rules="required"
            >
              <TextField
                v-model.trim="firstNames"
                :label="'First name(s)*'"
                :errors="errors"
                :name="'first_names'"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" sm="6" md="4" class="pb-0">
            <ValidationProvider
              id="last_name"
              v-slot="{ errors }"
              name="last_name"
              rules="required"
            >
              <TextField
                v-model.trim="lastName"
                :label="'Last name*'"
                :errors="errors"
                :name="'last_name'"
              />
            </ValidationProvider>
          </v-col>
        </v-row>

        <v-row class="mt-4">
          <v-col class="id-section" cols="12" lg="8">
            <v-row no-gutters>
              <v-col cols="12">
                <h5 class="text-subtitle-1 mb-0">
                  <strong>Identity number details(optional)</strong>
                </h5>
                <ValidationProvider
                  id="id_type"
                  v-slot="{ errors }"
                  name="id_type"
                  :rules="`${requiredNationality ? 'required' : ''}`"
                >
                  <RadioSelect
                    v-model="identityNumber.idType"
                    :items="[
                      { value: 'ID', text: 'National ID' },
                      { value: 'PASSPORT', text: 'Passport' },
                      { value: 'NONE', text: 'None' },
                    ]"
                    :color="'white'"
                    :errors="errors"
                    :group="'id_type'"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="12" md="7">
                <ValidationProvider
                  v-if="identityNumber.idType !== 'NONE'"
                  id="country_code"
                  v-slot="{ errors }"
                  name="country_code"
                  :rules="`${requiredNationality ? 'required' : ''}`"
                >
                  <ListSelect
                    v-model="identityNumber.countryCode"
                    :label="'Nationality*'"
                    :items="$options.countryDropdownOptions"
                    :errors="errors"
                    :name="'country_code'"
                    :group="'country_code'"
                  />
                </ValidationProvider>

                <ValidationProvider
                  v-if="
                    identityNumber.idType && identityNumber.idType !== 'NONE'
                  "
                  id="id_or_passport_number"
                  v-slot="{ errors }"
                  name="id_or_passport_number"
                  :rules="`${isSouthAfricanID ? 'id_number' : ''}${
                    requiredIdNumber ? '|required' : ''
                  }`"
                >
                  <TextField
                    v-model.trim="identityNumber.number"
                    :label="idOrPassportNumberLabel"
                    :errors="errors"
                    :name="'id_or_passport_number'"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <div class="button-container">
          <DefaultButton
            @defaultButtonClicked="$router.push('married')"
            :callout="'left'"
            :small="true"
          />
          <GradientButton
            :title="'Next'"
            :small="true"
            :type="'submit'"
            :callout="'right'"
            :loading="buttonLoading"
          />
        </div>
      </v-form>
    </ValidationObserver>

    <ErrorSnackbar v-model="showError" :error="errorMessage" />
  </div>
</template>

<script>
  import {
    CREATE_PERSON_MUTATION,
    UPDATE_PERSON_MUTATION,
  } from '../../constants/graphql/people';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import { userFacingGraphQLError } from '../../helpers/userFacingGraphQLError';
  import { countryDropdownOptions } from '../../constants/js/countries';

  export default {
    name: 'YourSpouse',
    components: {
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      testator: { type: Object, required: true },
      relations: { type: Object, required: true },
    },
    data() {
      return {
        errorMessage: null,
        spouse: this.relations.spouse,
        firstNames: this.relations.spouse.firstNames,
        lastName: this.relations.spouse.lastName,
        identityNumber: this.relations.spouse.identityNumber || {
          countryCode: 'ZA',
          idType: 'NONE',
        },
      };
    },
    computed: {
      isGBWill() {
        return this.testator.willCountry == 'GB';
      },
      isSouthAfricanID() {
        return (
          this.identityNumber.idType == 'ID' &&
          this.identityNumber.countryCode == 'ZA'
        );
      },
      requiredIdNumber() {
        return (
          this.identityNumber.idType == 'ID' ||
          this.identityNumber.idType == 'PASSPORT'
        );
      },
      requiredNationality() {
        return this.idNumberPresent();
      },
      idOrPassportNumberLabel() {
        if (this.identityNumber.idType == 'PASSPORT') {
          return 'Passport number*';
        } else if (this.identityNumber.idType == 'ID') {
          return 'National ID number*';
        } else {
          return 'ID or passport number*';
        }
      },
    },
    countryDropdownOptions: countryDropdownOptions,
    methods: {
      submit() {
        if (this.buttonLoading) return;
        this.buttonLoading = true;
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.spouse.id ? this.updateSpouse() : this.createSpouse();
          } else {
            this.buttonLoading = false;
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      createSpouse() {
        const relation = 'SPOUSE';
        const personInputDetails = {
          testatorId: this.testator.id,
          firstNames: this.firstNames,
          lastName: this.lastName,
          kinship: relation,
          identityNumber: this.idNumberDetails(),
        };

        this.$apollo
          .mutate({
            mutation: CREATE_PERSON_MUTATION,
            variables: {
              relation,
              personInputDetails,
            },
            update: (cache, { data: { createPerson } }) => {
              this.updateCacheAfterCreatePerson(cache, createPerson);
            },
          })
          .then(_data => {
            this.redirectToNext();
            this.buttonLoading = false;
          })
          .catch(error => {
            this.errorMessage = userFacingGraphQLError(error);
            this.displayError();
            if (!this.errorMessage) throw error;
          });
      },
      updateCacheAfterCreatePerson(cache, createPerson) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testator.id },
        };
        const data = cache.readQuery(query);

        // update list of people
        data.testator.people.push(createPerson);

        cache.writeQuery({ ...query, data: data });
      },
      updateSpouse() {
        if (this.idTypeSelected) {
          this.identityNumber.countryCode = this.identityNumber.countryCode;
        }

        const personInputDetails = {
          firstNames: this.firstNames,
          lastName: this.lastName,
          identityNumber: this.idNumberDetails(),
        };
        const id = this.spouse.id;

        this.$apollo
          .mutate({
            mutation: UPDATE_PERSON_MUTATION,
            variables: {
              id,
              personInputDetails,
            },
            update: (cache, { data: { updatePerson } }) => {
              this.updateCacheAfterUpdatePerson(cache, updatePerson);
            },
          })
          .then(_data => {
            this.redirectToNext();
            this.buttonLoading = false;
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      idTypeSelected() {
        return (
          this.identityNumber.idType == 'ID' ||
          this.identityNumber.idType == 'PASSPORT'
        );
      },
      idNumberDetails() {
        if (this.idNumberPresent() && this.identityNumber.idType !== 'NONE') {
          return {
            countryCode: this.identityNumber.countryCode,
            idType: this.identityNumber.idType,
            number: this.identityNumber.number,
            refresh: true,
          };
        } else {
          return { destroy: true };
        }
      },
      idNumberPresent() {
        return (
          this.identityNumber.number !== undefined &&
          this.identityNumber.number !== ''
        );
      },
      updateCacheAfterUpdatePerson(cache, updatePerson) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testator.id },
        };
        const data = cache.readQuery(query);

        var spouse = data.testator.people.find(p => p.id == updatePerson.id);
        spouse = updatePerson;

        cache.writeQuery({ ...query, data: data });
      },
      redirectToNext() {
        if (this.isGBWill) {
          if (this.wizard == 'spouse') {
            this.$router.push({ name: 'PeopleOverview', hash: '#spouse' });
          } else {
            this.$router.push('children');
          }
        } else {
          this.$router.push('marital-regime');
        }
      },
    },
  };
</script>

<style>
  .your-spouse {
    .v-radio {
      margin-right: 8px !important;
    }
  }
  .button-container {
    margin-top: 40px;
    display: flex;
    gap: 20px;
  }
</style>
